<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import { ButtonVariant } from '@tundr/theme';
  import {
    FeedbackMessage,
    FeedbackMessageVariant,
  } from '@tundr/ds-components';
  import { useModal } from '../../composables/use-modal';
  import ModalActions from '../ModalActions/ModalActions.vue';
  import ModalContent from '../ModalContent/ModalContent.vue';
  import ModalSkeleton from '../ModalSkeleton/ModalSkeleton.vue';
  import ModalTitle from '../ModalTitle/ModalTitle.vue';
  import ModalTitleImage from '../ModalTitleImage/ModalTitleImage.vue';
  import {
    confirmModalContainerClass,
    confirmModalContentClass,
    confirmModalContentDisclaimerClass,
  } from './ConfirmModal.css.ts';

  const { t } = useLocale('common');

  const { destructive } = defineProps<{ destructive?: boolean }>();

  const { closeModal, props, events, labels } = useModal();

  const confirmLabelVariant = destructive
    ? ButtonVariant.error
    : ButtonVariant.primary;

  const emits = defineEmits<{
    confirm: [];
    cancel: [];
  }>();

  const onCancel = () => {
    emits('cancel');
    if (events?.onCancel) {
      events.onCancel();
    }
    closeModal();
  };

  const onConfirm = () => {
    emits('confirm');
    if (events?.onConfirm) {
      events.onConfirm();
    }
    closeModal();
  };
</script>

<template>
  <ModalSkeleton :class="confirmModalContainerClass">
    <template #title>
      <ModalTitle>
        <ModalTitleImage v-if="props?.image" :src="props.image as string" />
        {{ props!.title }}
      </ModalTitle>
    </template>
    <ModalContent :class="confirmModalContentClass" v-if="props?.message">
      {{ props!.message }}
      <div
        v-if="props?.disclaimerTitle"
        :class="confirmModalContentDisclaimerClass"
      >
        <FeedbackMessage
          :variant="FeedbackMessageVariant.INFO"
          distinct-id="confirm-modal-disclaimer"
          :message="props!.disclaimerTitle as string"
          :description="props!.disclaimerDescription as string"
        />
      </div>
    </ModalContent>
    <template #actions>
      <ModalActions
        :confirm-test-id="labels?.confirmTestId"
        :cancel-test-id="labels?.cancelTestId"
        :confirm-label="labels?.confirm || t('actions.confirm')"
        :cancel-label="labels?.cancel || t('actions.cancel')"
        :confirm-label-variant="confirmLabelVariant"
        @confirm="onConfirm"
        @cancel="onCancel"
      />
    </template>
  </ModalSkeleton>
</template>
